import React, { useState, useEffect, useMemo } from 'react';
import OrganizationsTable from './organizationsTable';
import {
    Box, Container, Typography, Grid, TextField, useMediaQuery, Divider
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../styles/StyleTheme';
import buildingsBackground from '../../assets/images/HIR-FONDO.png';
import Loading from '../../components/Loading';
import Pagination from '@mui/material/Pagination';
import { publicAxios } from '../../apiClients';
import SelectOptionMM from '../../components/SelectOptionMM';
import MMLogo from '../../assets/banners/HM-blanco.png';
import Button from '@mui/material/Button';
import InstitutionButton from '../../components/InstitutionButton';
import { generateOrganizationRouteSegments } from '../../utils/slugify';
import Fuse from 'fuse.js';

const MarketMonitorHealth = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');
    const [firms, setFirms] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState({ field: 'position', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const firmsPerPage = 30;
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTablet = useMediaQuery('(max-width: 1100px)');
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const showCompactCarousel = !isDesktop;
    const sectionWidth = { xs: '100%', md: '90%', lg: '80%' };
    const CONTENT_MAX_PX = 1400;

    const fuse = useMemo(() => {
        if (firms.length === 0) return null;
        return new Fuse(firms, {
            keys: ['institutionName'],
            threshold: 0.3,
            includeScore: true,
            minMatchCharLength: 2,
            distance: 100,
            ignoreLocation: false,
            shouldSort: true,
            includeMatches: true,
            findAllMatches: false,
            useExtendedSearch: true,
        });
    }, [firms]);

    const searchFirms = (firmsList, query) => {
        if (!query.trim()) return firmsList;
        const exactMatches = firmsList.filter(firm =>
            firm.institutionName.toLowerCase().startsWith(query.toLowerCase())
        );
        if (exactMatches.length > 0) return exactMatches;
        if (fuse) {
            const fuseResults = fuse.search({
                $or: [
                    { institutionName: `^${query}` },
                    { institutionName: query }
                ]
            });
            return fuseResults.map(result => result.item);
        }
        return [];
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-health/table');
                const data = response.data;
                const firmsWithSlugs = data.map((firm) => {
                    const { organizationSlug, countrySlug } = generateOrganizationRouteSegments({
                        institutionName: firm.institutionName,
                        countryName: firm.country?.name ?? firm.Country?.name,
                        countryId: firm.country?.id ?? firm.Country?.id,
                        organizationId: firm.id,
                    });

                    return {
                        ...firm,
                        slug: organizationSlug,
                        countrySlug,
                    };
                });
                setFirms(firmsWithSlugs);
            } catch (error) {
                console.error('Error fetching firms:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        // Recuperar los filtros guardados
        const savedFilters = JSON.parse(localStorage.getItem('marketMonitorHealthFilters'));
        if (savedFilters) {
            setSearchTerm(savedFilters.searchTerm || '');
            setSelectedCountry(savedFilters.selectedCountry || '');
            setSelectedRegion(savedFilters.selectedRegion || '');
            setCurrentPage(savedFilters.currentPage || 1);
        }
    }, []);

    const handleSearchChange = (event) => {
        const newSearchTerm = event.target.value;
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
        saveFilters(newSearchTerm, selectedCountry, selectedRegion, 1);
    };

    const handleCountryChange = (newCountryId) => {
        setSelectedCountry(newCountryId || '');
        setSelectedRegion(''); // Limpiar la región cuando se selecciona un país
        setCurrentPage(1); // Reiniciar a la primera página al aplicar filtro
        saveFilters(searchTerm, newCountryId, '', 1);
    };

    const handleRegionChange = (newRegionId) => {
        setSelectedRegion(newRegionId || '');
        setSelectedCountry('');
        setCurrentPage(1);
        saveFilters(searchTerm, '', newRegionId, 1);
    };

    const saveFilters = (search, country, region, page) => {
        localStorage.setItem('marketMonitorHealthFilters', JSON.stringify({
            searchTerm: search,
            selectedCountry: country,
            selectedRegion: region,
            currentPage: page
        }));
    }

    const sortFirms = (firms) => {
        return [...firms].sort((a, b) => {
            if (sortBy.field === 'position') {
                const positionDiff = a.position - b.position;
                if (positionDiff === 0) {
                    return sortBy.direction === 'asc'
                        ? a.institutionName.localeCompare(b.institutionName)
                        : b.institutionName.localeCompare(a.institutionName);
                }
                return sortBy.direction === 'asc' ? positionDiff : -positionDiff;
            }

            if (sortBy.field === 'totalScore') {
                const scoreDiff = a.totalScore - b.totalScore;
                if (scoreDiff === 0) {
                    return sortBy.direction === 'desc'
                        ? a.institutionName.localeCompare(b.institutionName)
                        : b.institutionName.localeCompare(a.institutionName);
                }
                return sortBy.direction === 'asc' ? scoreDiff : -scoreDiff;
            }

            if (sortBy.field === 'institutionName') {
                return sortBy.direction === 'asc'
                    ? a.institutionName.localeCompare(b.institutionName)
                    : b.institutionName.localeCompare(a.institutionName);
            }

            if (sortBy.field === 'region') {
                return sortBy.direction === 'asc'
                    ? a.country.region.localeCompare(b.country.region)
                    : b.country.region.localeCompare(a.country.region);
            }

            if (sortBy.field === 'country') {
                return sortBy.direction === 'asc'
                    ? a.country.name.localeCompare(b.country.name)
                    : b.country.name.localeCompare(a.country.name);
            }

            return 0;
        });
    }

    const uniqueRegions = [...new Set(firms
        .filter((firm) => firm.country && firm.country.region)
        .map((firm) => firm.country.region))]
        .sort((a, b) => a.localeCompare(b))
        .map((region) => ({ id: region, name: region }));

    const uniqueCountries = selectedRegion
        ? [...new Set(firms
            .filter((firm) => firm.country && firm.country.region === selectedRegion)
            .map((firm) => firm.country.name))]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }))
        : [...new Set(firms
            .filter((firm) => firm.country && firm.country.name)
            .map((firm) => firm.country.name))]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }));

    const filteredFirms = searchFirms(firms, searchTerm).filter(
        (firm) =>

            (!selectedCountry || firm.country.name === selectedCountry) &&
            (!selectedRegion || firm.country.region === selectedRegion)
    );

    const sortedFirms = sortFirms(filteredFirms);
    const indexOfLastFirm = currentPage * firmsPerPage;
    const indexOfFirstFirm = indexOfLastFirm - firmsPerPage;
    const currentFirms = sortedFirms.slice(indexOfFirstFirm, indexOfLastFirm);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        saveFilters(searchTerm, selectedCountry, selectedRegion, value);
    };

    if (isLoading) {
        return (
            <Loading
                background="#6ec1e4"
                barTrackColor="rgba(255, 255, 255, 0.45)"
                barColor="#3592D2"
                logo={MMLogo}
                logoAlt="Health Market Monitor"
            />
        );
    }
    const renderContactButton = (props = {}) => (
        <Button
            component="a"
            href="https://thelegalindustry.com/contact-us/"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            sx={{
                backgroundColor: 'white',
                color: 'black',
                borderRadius: 4,
                height: props.height ?? 30,
                width: '100%',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: "#3592D2" },
            }}
        >
            Contact us
        </Button>
    );

    // === Sección de filtros
    const FiltersSection = (
        <Container
            sx={{
                mt: { xs: '15px', sm: '30px' },
                mb: { xs: 2, sm: 4 },
                width: sectionWidth,
                maxWidth: `${CONTENT_MAX_PX}px`,
                px: { xs: 0, sm: 2, md: 3 },
            }}
        >
            <Grid
                container
                spacing={3}
                alignItems="center"
                sx={{ justifyContent: 'center', px: { xs: 0, md: 5 } }}
            >
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Search firm..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{
                            width: '100%',
                            padding: '8px 0',
                            '& .MuiOutlinedInput-root': {
                                height: '33px',
                                '& fieldset': {
                                    borderColor: 'black',
                                    borderRadius: '18px',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'black',
                                    borderRadius: '18px',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: "#3592D2",
                                    borderRadius: '18px',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: 'black',
                                fontSize: '0.8rem',
                            },
                            '& .MuiInputBase-input': {
                                color: 'black',
                            },
                        }}
                        InputLabelProps={{ style: { color: 'black' } }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <SelectOptionMM
                        options={uniqueRegions.length > 0 ? uniqueRegions : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleRegionChange}
                        name="Filter by Region"
                        width="100%"
                        selectedOption={selectedRegion}
                        mmcolor="black"
                        focusColor="#3592D2"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <SelectOptionMM
                        options={uniqueCountries.length > 0 ? uniqueCountries : [{ id: '', name: 'No options' }]}
                        onSelectionChange={handleCountryChange}
                        name="Filter by Country"
                        width="100%"
                        selectedOption={selectedCountry}
                        mmcolor="black"
                        focusColor="#3592D2"
                    />
                </Grid>
            </Grid>
        </Container>
    );

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    backgroundImage: `linear-gradient(
            to bottom,
            rgba(53, 146, 210, 0.25) 0%,
            rgba(20, 60, 100, 0.53) 100%
        ), url(${buildingsBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    p: { xs: 3, md: 10 },
                    textAlign: 'start',
                }}
            >
                <Box sx={{ position: 'absolute', top: isTablet ? '2%' : '4%', left: '4%' }}>
                    <InstitutionButton institution='health' />
                </Box>
                <Grid
                    item
                    xs={12}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >



                    {/* Imagen */}
                    <Box
                        component="img"
                        src={MMLogo}
                        alt="Market Monitor"
                        sx={{
                            width: isMobile ? '35%' : '20%',
                            height: isMobile ? '35%' : '20%',
                            marginLeft: isMobile ? '5%' : '2%',
                            // Agrega esta línea para la sombra:
                            filter: 'drop-shadow(5px -2px 10px rgba(0, 0, 0, 0.5))',
                        }}
                    />




                    {/* Texto */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'white',
                            mt: isMobile ? 1 : 3,
                            textAlign: 'center',
                            lineHeight: 1.2,
                            fontSize: isMobile ? '0.65rem' : '1.2rem',
                            width: '70%',
                            filter: 'drop-shadow(5px 1px 10px rgba(0, 0, 0, 0.8))',
                        }}
                    >
                        Monitor the real-time{' '}
                        <span style={{ fontSize: '115%' }}>positioning</span> of institutions in your Country and compare it with their global{' '}
                        <span style={{ fontSize: '115%' }}>competition</span>
                    </Typography>
                </Grid>
            </Box>
            <Box
                sx={{
                    backgroundColor: '#D3F1FF',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'black',
                    py: isMobile ? 2 : 4,
                    paddingBottom: '75px',
                    overflow: 'hidden',
                    px: { xs: 1.25, sm: 0 },
                }}
            >
                <Container
                    sx={{
                        width: sectionWidth,
                        maxWidth: `${CONTENT_MAX_PX}px`,
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        mb: '2%',
                        color: 'black',
                        px: { xs: 0, sm: 2, md: 3 },
                    }}
                >
                    <Box
                        component="img"
                        src="https://healthindustryreviews.com/wp-content/uploads/2023/10/Grupo-170.svg"
                        alt="Health Industry Reviews"
                        sx={{
                            width: { xs: '55%', sm: '35%', md: '20%' },
                            height: 'auto',
                            marginRight: { md: '2%' },
                            alignSelf: { xs: 'center', md: 'flex-start' },
                            mb: { xs: 1, md: 0 },
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            sx={{
                                fontSize: isMobile ? '0.5rem' : '75%',
                                textAlign: isMobile ? 'center' : 'left',
                                mb: isMobile ? 1 : 0,
                                marginRight: '2%',
                            }}
                        >
                            Our AI model is fed with information coming from public sources or provided by the institutions (verified by our team).

                            <br />
                            Do you want to register a new institution or update displayed information?
                        </Typography>
                    </Box>
                    {!showCompactCarousel && (
                        <Box sx={{ mt: 0.5, textAlign: { xs: 'center', md: 'left' } }}>
                            {renderContactButton()}
                        </Box>
                    )}
                </Container>

                {!showCompactCarousel && (
                    <>
                        <Divider
                            sx={{
                                borderColor: 'black',
                                borderWidth: 1,
                                marginTop: '1%',
                                marginBottom: '0.2%',
                                width: '80%',
                                alignSelf: 'center',
                            }}
                        />
                        {FiltersSection}
                    </>
                )}

                {showCompactCarousel && (
                    <>
                        <Container
                            sx={{
                                width: sectionWidth,
                                maxWidth: `${CONTENT_MAX_PX}px`,
                                textAlign: 'center',
                                mb: 1,
                                px: { xs: 0, sm: 2, md: 3 },
                            }}
                        >
                            {renderContactButton({ height: 34 })}
                        </Container>

                        <Divider
                            sx={{
                                borderColor: 'black',
                                borderWidth: 1,
                                mt: 1,
                                mb: 2,
                                width: '92%',
                                mx: 'auto',
                            }}
                        />
                        {FiltersSection}
                    </>
                )}

                <Container
                    sx={{
                        width: sectionWidth,
                        maxWidth: `${CONTENT_MAX_PX}px`,
                        px: { xs: 0, sm: 1, md: 2 },
                    }}
                >
                    <Grid
                        container
                        columnSpacing={2}
                        rowSpacing={3}
                        alignItems="stretch"
                        wrap="nowrap"
                    >
                        <Grid
                            item
                            xs={12}
                            sx={{
                                flex: '1 1 auto',
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <OrganizationsTable
                                    firms={currentFirms}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                />
                                <Pagination
                                    count={Math.ceil(filteredFirms.length / firmsPerPage)}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    sx={{
                                        mt: 0,
                                        justifyContent: "center",
                                        display: "flex",
                                        "& .MuiPaginationItem-root": {
                                            color: "black",
                                            borderColor: "#3592D2",
                                        },
                                        "& .MuiPaginationItem-root.Mui-selected": {
                                            backgroundColor: "#3592D2",
                                            color: "white",
                                        },
                                        "& .MuiPaginationItem-root.Mui-selected:hover": {
                                            backgroundColor: "#2c7bb3", // un poquito más oscuro al hover (opcional)
                                        },
                                        "& .MuiPaginationItem-root:hover": {
                                            backgroundColor: "rgba(53, 146, 210, 0.12)",
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default MarketMonitorHealth;
